import { call, put, takeEvery } from 'redux-saga/effects';

import { MeActionTypes } from '../actions/types';
import { MeActionCreators, ModalsActionCreators } from '../actions/creators';
import { AsyncActionStatus } from '../enums';
import { generateErrorMessage } from '../helpers';
import API from '../../api';
import Mixpanel from '../../../helpers/mixpanel';
import { userRoleCheckers } from '../../../../shared/helpers';
import { ROUTES } from '../../router';
import { toast } from '../../utilities';
import { UserInteractions } from '../../../../shared/enums';


function* loginUser(action) {
  try {
    const { email, password } = action.payload;
    const response = yield call(
      API.Users.loginUser,
      email,
      password,
    );
    const { user } = response.data;

    Mixpanel.identify(user._id);
    const userProfileData = {
      $userEmail: user.email,
      $userRole: user.role,
      userId: user._id,
    };
    if (userRoleCheckers.isCustomer(user.role) && user.project?.type) {
      userProfileData.$projectType = user.project.type;
    }
    Mixpanel.people.set(userProfileData);

    yield put(MeActionCreators.loginUser[AsyncActionStatus.FULFILLED](user));
    /*
     * TODO:
     * if the translations approach changes do not forget about next places:
     * - initApp
     * - updateUser
     * - accessToken.primaryLanguage
     */
    yield call(i18next.setCurrentLanguage, user.primaryLanguage);
    if (user.permissions.accessToProjectBlocked) {
      yield put(ModalsActionCreators.openAccessToProjectBlockedModal());
    }
  } catch (err) {
    Mixpanel.track('Unsuccessful login', { providedEmail: action.payload.email });

    const errMessage = generateErrorMessage(err);
    yield put(MeActionCreators.loginUser[AsyncActionStatus.REJECTED](errMessage));
    yield call(toast.error, errMessage);
  }
}

function* getMe(action) {
  try {
    const response = yield call(API.Users.getMe);
    /*
     * TODO: Block for future features (parallel fetch): Permissions, AdditionalData...
     * yield all([
     *  call(getMe, '/me'),
     *  call(getPermissions, '/permissions')
     * ]);
     */
    yield put(MeActionCreators.getMe[AsyncActionStatus.FULFILLED](response.data.user));
    if (response.data.user.permissions.accessToProjectBlocked) {
      yield put(ModalsActionCreators.openAccessToProjectBlockedModal());
    }
  } catch (error) {
    const errMessage = generateErrorMessage(error);
    yield put(MeActionCreators.getMe[AsyncActionStatus.REJECTED](errMessage));
    yield call(toast.error, errMessage);
  }
}

function* logoutUser(action) {
  try {
    yield call(API.Users.logoutUser);
    yield put(MeActionCreators.logoutUser[AsyncActionStatus.FULFILLED]());

    if (userRoleCheckers.isCustomer(action.payload.role) || userRoleCheckers.isWriter(action.payload.role)) {
      // Added in ST-3271 for refreshing and deleting scrips related to ChatbaseBot
      window.location.href = ROUTES.home;
    } else {
      yield call(action.payload.navigate, ROUTES.home);
    }
  } catch (error) {
    yield put(MeActionCreators.logoutUser[AsyncActionStatus.REJECTED](
      generateErrorMessage(error),
    ));
  } finally {
    localStorage.removeItem('projectFilters');
    localStorage.removeItem('accountFilters');
    localStorage.removeItem('writerMatchFilters');
    localStorage.removeItem('projectsFeedbackFilters');
    localStorage.removeItem('onboardingFeedbackFilters');
    localStorage.removeItem('writerFeedbackFilters');
    localStorage.removeItem('upgradeFilters');
  }
}

function* updateUserInteractions({ payload: { userId, interactionKey } }) {
  try {
    const response = yield call(API.Users.updateUserInteractions, userId, interactionKey);
    yield put(MeActionCreators.updateUserInteractions[AsyncActionStatus.FULFILLED](response.data));
    if (interactionKey === UserInteractions.SeenChatbot) {
      yield put(ModalsActionCreators.closeChatBotInfoModal());
    } else if (interactionKey === UserInteractions.isOnboardingPassed) {
      yield put(ModalsActionCreators.closeOnboardingModal());
    }
  } catch (error) {
    yield put(MeActionCreators.updateUserInteractions[AsyncActionStatus.REJECTED](
      generateErrorMessage(error),
    ));
  }
}

export default [
  takeEvery(
    MeActionTypes.loginUser[AsyncActionStatus.PENDING],
    loginUser,
  ),
  takeEvery(
    MeActionTypes.getMe[AsyncActionStatus.PENDING],
    getMe,
  ),
  takeEvery(
    MeActionTypes.logoutUser[AsyncActionStatus.PENDING],
    logoutUser,
  ),
  takeEvery(
    MeActionTypes.updateUserInteractions[AsyncActionStatus.PENDING],
    updateUserInteractions,
  ),
];
